import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../utils/log';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import CoachesListContext from '../../../../context/CoachesListContext';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import InputLabel from '../../../../../components/v2/InputLabel';
import { Card, Container } from '../../../../../components/v2/Card';
import { SaveButton } from '../../../../../components/Button/ActionButtons';
import { StyledTextFieldContainer } from '../../../../../components/Inputs';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import ConfirmationModal from '../../../../../components/v2/ConfirmationModal/ConfirmationModal';
import CoachHistory, { HistoryEventType } from '../../../../Model/CoachHistory';
import InternalAssignment from '../../../../../Model/InternalAssignment';
import { AssignmentRole } from '../../../../../utils/internalAssignments';

import texts from '../texts.json';

const ToggleOptions = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
};

const ACEnabledSettings = ({ coachId, coach }) => {
  const { coachesCollection } = useContext(CoachesListContext);
  const { userId, userDoc } = useContext(LoggedInUserContext);
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const isComponentMountedRef = useComponentMounted();

  const [isACEnabled, setIsACEnabled] = useState(ToggleOptions.DISABLED);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLeavingPool, setIsLeavingPool] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState('');

  const coachDoc = useMemo(
    () => coachesCollection.docs.find((doc) => doc.id === coachId),
    [coachesCollection.docs, coachId],
  );

  useEffect(() => {
    if (!coachDoc) return;
    setIsACEnabled(coachDoc.isACEnabled ? ToggleOptions.ENABLED : ToggleOptions.DISABLED);
  }, [coachDoc]);

  const handleSave = useCallback(async () => {
    if (!coachDoc) return;
    setShowConfirm(false);
    setIsLoading(true);
    const newValue = isACEnabled === ToggleOptions.ENABLED;
    try {
      await coachDoc.updateFields({
        isACEnabled: newValue,
        lastUpdatedByUserName: userDoc.name,
      });
      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.AC_FLAG_UPDATED,
        coachId,
        actionedBy: userId,
        actionedByUserName: userDoc?.name,
        metadata: {
          previousValue: isACEnabled,
          newValue,
          ...(note && { note }),
        },
      });
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
      showToast(texts.saved);
      logCoachingActivity(CoachingActivity.UPDATED_COACH_SETTINGS);
    } catch (error) {
      showToast(format(texts.saveFailed, { error }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setNote('');
      }
    }
  }, [
    isComponentMountedRef,
    logCoachingActivity,
    isACEnabled,
    showToast,
    coachDoc,
    coachId,
    userDoc,
    userId,
    note,
  ]);

  const handleClose = useCallback(() => {
    setShowConfirm(false);
    setNote('');
  }, []);

  /*
    Turning AC off also takes a pooled coach out of the pool (the server closes their POOL row), so the
    confirmation says so. The pool is only looked up when it matters, and a failed lookup falls back to the
    plain copy rather than holding up the save.
  */
  const isCoachPooled = useCallback(async () => {
    try {
      const poolAssignments = await InternalAssignment.getCurrentAssignmentOfCoachByRole(coachId, AssignmentRole.POOL);
      return poolAssignments.docs.length > 0;
    } catch (error) {
      return false;
    }
  }, [coachId]);

  const handleOpenConfirm = useCallback(async () => {
    if (!coachDoc) return;
    const savedValue = coachDoc.isACEnabled ? ToggleOptions.ENABLED : ToggleOptions.DISABLED;
    if (isACEnabled === savedValue) return;

    let isPooled = false;
    if (isACEnabled === ToggleOptions.DISABLED) {
      setIsLoading(true);
      isPooled = await isCoachPooled();
      if (!isComponentMountedRef.current) return;
      setIsLoading(false);
    }
    setIsLeavingPool(isPooled);
    setShowConfirm(true);
  }, [coachDoc, isACEnabled, isCoachPooled, isComponentMountedRef]);

  return (
    <>
      <Card>
        <Container>
          <StyledTextFieldContainer $withMargin>
            <InputLabel>{texts.acEnabled}</InputLabel>
            <RadioButtonGroup
              options={[
                { label: texts.enabled, value: ToggleOptions.ENABLED },
                { label: texts.disabled, value: ToggleOptions.DISABLED },
              ]}
              selectedOption={isACEnabled}
              onOptionChange={setIsACEnabled}
            />
          </StyledTextFieldContainer>
          <SaveButton
            onClick={handleOpenConfirm}
            disabled={isLoading}
            $fitToContent
          >
            {texts.saveButton}
          </SaveButton>
        </Container>
      </Card>
      <ConfirmationModal
        open={showConfirm}
        onClose={handleClose}
        onConfirm={handleSave}
        title={format(texts.acEnabledConfirmTitle, { coach })}
        description={format(isLeavingPool ? texts.acDisabledLeavesPoolConfirmContent : texts.acEnabledConfirmContent, {
          value: isACEnabled === ToggleOptions.ENABLED ? texts.enabled : texts.disabled,
          coach,
        })}
        showAdditionalInput
        onInputChange={setNote}
      />
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

ACEnabledSettings.propTypes = {
  coachId: PropTypes.string.isRequired,
  coach: PropTypes.string.isRequired,
};

export default compose(observer)(ACEnabledSettings);
